/* eslint-disable camelcase, unicorn/no-null */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  forwardRef
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup
} from '@angular/forms';
import { LanguageVisibility } from '@state/settings';
import type { DndDropEvent } from 'ngx-drag-drop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QuestionInput } from 'src/generated/base-types';
import { FormResponseOptionInput } from '../form-types';
import {
  isSecureIndexRange,
  moveObject,
  normalizeIndexRange,
  transformDropEventToIndexRange
} from '../services/drag-and-drop';
import { ValidationsQuestionAMinusGraphQL } from '../services/graphql-question-group-types';

@Component({
  selector: 'qf-question-content-type-a-minus-form',
  templateUrl: './question-content-type-a-minus-form.component.html',
  styleUrls: ['./question-content-type-a-minus-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuestionContentTypeAMinusFormComponent),
      multi: true
    }
  ]
})
export class QuestionContentTypeAMinusFormComponent
  implements OnDestroy, ControlValueAccessor
{
  @Input() public languageVisibility: LanguageVisibility;

  @Input()
  public set validations(value: ValidationsQuestionAMinusGraphQL | undefined) {
    this._validations = value;
    this.validations$.next(value);
  }
  public get validations(): ValidationsQuestionAMinusGraphQL | undefined {
    return this._validations;
  }

  public isRearranging = false;
  public form: UntypedFormGroup;

  private _validations: ValidationsQuestionAMinusGraphQL | undefined;

  private question$ = new Subject<QuestionInput>();
  private validations$ = new Subject<
    ValidationsQuestionAMinusGraphQL | undefined
  >();
  private destroy$ = new Subject<void>();

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    public readonly builder: UntypedFormBuilder
  ) {
    this.form = this.builder.group({
      contentDe: null,
      contentFr: null,
      contentEn: null,
      contentIt: null,
      videoDe: null,
      videoFr: null,
      videoEn: null,
      videoIt: null,
      responseOptions: this.builder.array([])
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(changes => {
      this.onChange({
        video: {
          de: changes.videoDe,
          fr: changes.videoFr,
          en: changes.videoEn,
          it: changes.videoIt
        },
        aMinus: {
          content: {
            de: changes.contentDe,
            fr: changes.contentFr,
            en: changes.contentEn,
            it: changes.contentIt
          },
          solution: changes.responseOptions.find(
            (responseOption: FormResponseOptionInput) => responseOption.correct
          )?.id,
          responseOptions: changes.responseOptions.map(
            (responseOption: FormResponseOptionInput) => ({
              id: responseOption.id,
              content: {
                de: responseOption.contentDe,
                fr: responseOption.contentFr,
                en: responseOption.contentEn,
                it: responseOption.contentIt
              }
            })
          )
        }
      });
      this.onTouch();
    });

    this.question$.pipe(takeUntil(this.destroy$)).subscribe(question => {
      this.responseOptionsForm.clear();
      (question.aMinus?.responseOptions || []).forEach(() => {
        this.responseOptionsForm.push(
          this.builder.group({
            id: null,
            correct: null,
            contentDe: null,
            contentFr: null,
            contentEn: null,
            contentIt: null
          })
        );
      });
      this.form.setValue({
        contentDe: question.aMinus?.content.de ?? null,
        contentFr: question.aMinus?.content.fr ?? null,
        contentEn: question.aMinus?.content.en ?? null,
        contentIt: question.aMinus?.content.it ?? null,
        videoDe: question.video?.de ?? null,
        videoFr: question.video?.fr ?? null,
        videoEn: question.video?.en ?? null,
        videoIt: question.video?.it ?? null,
        responseOptions: (question.aMinus?.responseOptions || []).map(
          option => ({
            id: option.id,
            correct: option.id === question.aMinus?.solution,
            contentDe: option.content?.de ?? null,
            contentFr: option.content?.fr ?? null,
            contentEn: option.content?.en ?? null,
            contentIt: option.content?.it ?? null
          })
        )
      });
    });
  }

  public writeValue(question: QuestionInput): void {
    this.question$.next(question);
  }

  public registerOnChange(fn: (value: QuestionInput) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get responseOptionsForm(): UntypedFormArray {
    return this.form.get('responseOptions') as UntypedFormArray;
  }

  public responseOptionClicked(responseOptionFormGroup: AbstractControl): void {
    this.responseOptionsForm.controls.forEach(control => {
      if (control instanceof UntypedFormGroup) {
        control.get('correct')?.setValue(false);
      }
    });
    responseOptionFormGroup.get('correct')?.setValue(true);
  }

  public responseOptionMoved(event: DndDropEvent): void {
    this.isRearranging = true;
    this.changeDetector.detectChanges();

    const indexRange = transformDropEventToIndexRange(event);
    if (!isSecureIndexRange(indexRange)) {
      return;
    }

    const normalizedIndexRange = normalizeIndexRange(indexRange);
    this.moveResponseOption(normalizedIndexRange.from, normalizedIndexRange.to);

    this.isRearranging = false;
    this.changeDetector.detectChanges();
  }

  private moveResponseOption(from: number, to: number): void {
    if (this.validations?.responseOptions) {
      this.validations = {
        ...this.validations,
        responseOptions: moveObject(this.validations.responseOptions, from, to)
      };
    }

    const movedFormGroup = this.responseOptionsForm.at(from);
    this.responseOptionsForm.removeAt(from);
    this.responseOptionsForm.insert(to, movedFormGroup);
  }

  private onChange: (value: QuestionInput) => void = () => void 0;
  private onTouch: () => void = () => void 0;
}
